import { useEpisodeStore } from '@/state/episodeStore';
import { MediaImage } from './MediaImage';
import { X } from '@phosphor-icons/react';
import clsx from 'clsx';

interface SceneChipProps {
  kind: 'scene';
  sceneId: string;
  episodeUuid: string;
  onRemove: () => void;
}

interface CastChipProps {
  kind: 'cast';
  castId: string;
  episodeUuid: string;
  onRemove: () => void;
}

type Props = SceneChipProps | CastChipProps;

export function ReferenceChip(props: Props) {
  if (props.kind === 'scene') return <SceneChip {...props} />;
  return <CastChip {...props} />;
}

function SceneChip({ sceneId, episodeUuid, onRemove }: SceneChipProps) {
  const scene = useEpisodeStore((s) => s.scenes.find((x) => x.id === sceneId));
  const heroImage = useEpisodeStore((s) => {
    if (!scene) return null;
    const heroId = scene.heroImageId ?? scene.imageIds.at(-1);
    return heroId ? s.images[heroId] ?? null : null;
  });

  const label = scene ? `Scene ${scene.order}` : 'Scene';

  return (
    <ChipShell ariaLabel={`Remove ${label} reference`} onRemove={onRemove} title={label}>
      {heroImage ? (
        <MediaImage
          episodeUuid={episodeUuid}
          path={heroImage.path}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-surface-3 font-mono text-[10px] tabular-nums text-text-2">
          {scene ? String(scene.order).padStart(2, '0') : '?'}
        </div>
      )}
    </ChipShell>
  );
}

function CastChip({ castId, episodeUuid, onRemove }: CastChipProps) {
  const character = useEpisodeStore((s) => s.characters.find((c) => c.id === castId));
  const label = character ? `${character.name} (${character.type})` : 'Cast';

  return (
    <ChipShell ariaLabel={`Remove ${label} reference`} onRemove={onRemove} title={label}>
      {character?.imagePath ? (
        <MediaImage
          episodeUuid={episodeUuid}
          path={character.imagePath}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-accent-soft font-mono text-[11px] font-semibold text-text-1">
          {character ? initials(character.name) : '?'}
        </div>
      )}
    </ChipShell>
  );
}

function ChipShell({
  children,
  onRemove,
  ariaLabel,
  title,
}: {
  children: React.ReactNode;
  onRemove: () => void;
  ariaLabel: string;
  title: string;
}) {
  return (
    <span className="group/chip relative inline-block" title={title}>
      <span className="block h-7 w-7 overflow-hidden rounded-sm transition group-hover/chip:brightness-75">
        {children}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={ariaLabel}
        className={clsx(
          'absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-text-on-accent',
          'opacity-0 shadow-sm transition group-hover/chip:opacity-100 focus-visible:opacity-100',
        )}
      >
        <X size={10} weight="bold" />
      </button>
    </span>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
